/**
 * Image bytes → ImageBitmap for scan textures (no network, no Firebase), shared
 * by the CRM scan file proxy client and the scan model loader.
 */

export const DEFAULT_MAX_TEXTURE_BYTES = 12 * 1024 * 1024;

export function sniffImageMime(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 4) return null;
  const u8 = new Uint8Array(buffer);
  if (u8[0] === 0xff && u8[1] === 0xd8) return "image/jpeg";
  if (u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) return "image/png";
  if (u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46) return "image/webp";
  return null;
}

/** Pixel size from a JPEG (SOFn) or PNG (IHDR) header; null when unknown. */
export function imageDimensions(buffer) {
  if (!(buffer instanceof ArrayBuffer)) return null;
  const u8 = new Uint8Array(buffer);
  const mime = sniffImageMime(buffer);
  if (mime === "image/png" && u8.length >= 24) {
    const view = new DataView(buffer);
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (mime !== "image/jpeg") return null;
  let i = 2;
  while (i + 9 < u8.length) {
    if (u8[i] !== 0xff) return null;
    const marker = u8[i + 1];
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      i += 2;
      continue;
    }
    const length = (u8[i + 2] << 8) | u8[i + 3];
    const isFrame =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isFrame) {
      return { height: (u8[i + 5] << 8) | u8[i + 6], width: (u8[i + 7] << 8) | u8[i + 8] };
    }
    i += 2 + length;
  }
  return null;
}

function scaledSize({ width, height }, maxEdge) {
  const scale = maxEdge / Math.max(width, height);
  return {
    resizeWidth: Math.max(1, Math.round(width * scale)),
    resizeHeight: Math.max(1, Math.round(height * scale)),
    resizeQuality: "high",
  };
}

/**
 * Decodes texture bytes. Texture.flipY is ignored for ImageBitmap sources, so
 * the OBJ/MTL convention is baked into the bitmap (UVs line up with
 * TextureLoader behavior). `maxEdge` decodes straight to a smaller bitmap
 * (createImageBitmap resize) so a large atlas never sits in memory at full size.
 */
export async function decodeImageBitmap(buffer, { maxEdge = 0, maxBytes = DEFAULT_MAX_TEXTURE_BYTES } = {}) {
  const mime = sniffImageMime(buffer);
  if (!mime) {
    throw new Error(`Not a decodable image (${buffer?.byteLength ?? 0} bytes)`);
  }
  if (buffer.byteLength > maxBytes) {
    throw new Error(`Image too large for preview texture (${buffer.byteLength} bytes)`);
  }
  const blob = new Blob([buffer], { type: mime });
  const options = { imageOrientation: "flipY", premultiplyAlpha: "none" };
  const size = maxEdge ? imageDimensions(buffer) : null;
  if (size && Math.max(size.width, size.height) > maxEdge) {
    return createImageBitmap(blob, { ...options, ...scaledSize(size, maxEdge) });
  }
  const bitmap = await createImageBitmap(blob, options);
  if (maxEdge && !size && Math.max(bitmap.width, bitmap.height) > maxEdge) {
    const resized = await createImageBitmap(bitmap, {
      premultiplyAlpha: "none",
      ...scaledSize({ width: bitmap.width, height: bitmap.height }, maxEdge),
    });
    bitmap.close();
    return resized;
  }
  return bitmap;
}
